/**
 * FauxDS Select - Recreated from Figma MCP
 *
 * ⚠️ This is a SIMPLIFIED RECREATION of the Slate Select component for prototyping.
 * It is NOT the real @faire/slate Select and should NOT be used for:
 * - Engineering handoff
 * - Design specs
 * - Production code
 *
 * For the real Slate Select, see: @faire/slate/components/select
 *
 * This component was recreated from Figma MCP design specs:
 * - Figma node: 7175:9209 (Slate Select)
 * - Design tokens extracted from Figma variables
 * - All variants verified: State (Default, Filled, Active), Disabled (true/false), Error (true/false)
 * - Label, helper, and error text support verified
 * - Focused state verified
 */
import { cn } from "@faire/prototype-playground/lib/utils";
import React, { forwardRef, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";

export type SelectState = "Default" | "Filled" | "Active";

export interface SelectOption {
  /**
   * The display label for the option
   */
  label: string;
  /**
   * The value of the option
   */
  value: string;
  /**
   * Whether the option is disabled
   */
  disabled?: boolean;
  /**
   * When set, shown on the closed trigger instead of `label`. Dropdown rows still use `label`.
   * Playground convenience for prefixes like “Sort: …”.
   */
  triggerLabel?: string;
}

export interface SelectProps {
  /**
   * Label text; when provided, the label is shown (aligned with Slate `label`)
   *
   * **playground only**
   * Label is optional in playground but required in Slate.
   */
  label?: string;
  /**
   * Placeholder text shown when no option is selected
   * @default "Placeholder text"
   */
  placeholder?: string;
  /**
   * Helper text displayed below the select; when provided, helper copy is shown (aligned with Slate `helper`)
   */
  helper?: string;
  /**
   * Error state: `true` for styling only, or a string for the error message (aligned with Slate `error`)
   */
  error?: boolean | string;

  /**
   * Accessibility label for screen readers
   * @default label when set
   */
  "aria-label"?: string;
  /**
   * Whether the select is disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * **playground only**
   * Visual state of the select
   * @default "Default"
   */
  state?: SelectState;
  /**
   * Options array
   */
  options?: SelectOption[];
  /**
   * Current value (controlled)
   */
  value?: string;
  /**
   * **playground only**
   * Default value (uncontrolled)
   */
  defaultValue?: string;
  /**
   * Change handler
   */
  onChange?: (value: string) => void;
  /**
   * **playground only**
   * Select name attribute
   */
  name?: string;
  /**
   * **playground only**
   * Optional className for additional styling
   */
  className?: string;
  /**
   * **playground only**
   * Whether the select is required
   * @default false
   */
  required?: boolean;
}

// Chevron down icon
const ChevronDownIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 6L8 10L12 6"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Chevron up icon
const ChevronUpIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 10L8 6L12 10"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Checkmark icon
const CheckIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3 8L6 11L13 4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Select - FauxDS (recreated from Figma MCP)
 *
 * A Select presents a list of options from which a user can select one option, or several.
 * A selected option can represent a value in a form, or can be used as an action to filter or sort existing content.
 *
 * Usage tips:
 * - Select can be used in forms on full pages, in modals, or on side panels
 * - When organizing Select menu items, sort the list in a logical order
 * - It is best practice not to use a Select if there are two options to choose from (use radio buttons instead)
 * - Do not nest Select components or use them to display overly complex information
 *
 * This implementation was created by extracting design specs directly from Figma.
 */
const Select = forwardRef<HTMLDivElement, SelectProps>(
  (
    {
      label,
      placeholder = "Placeholder text",
      helper,
      error,
      "aria-label": ariaLabel,
      disabled = false,
      state = "Default",
      options = [
        { value: "option1", label: "Option 1" },
        { value: "option2", label: "Option 2" },
        { value: "option3", label: "Option 3" },
        { value: "option4", label: "Option 4" },
      ],
      value,
      defaultValue,
      onChange,
      className = "",
      required = false,
    },
    ref
  ) => {
    const hasError = !!error;
    const errorMessage = typeof error === "string" ? error : undefined;

    const generatedId = useId();
    const selectId = `select-figma-${generatedId}`;
    const helperId = `helper-figma-${generatedId}`;
    const [isOpen, setIsOpen] = useState(state === "Active");
    const [internalValue, setInternalValue] = useState<string>(
      value || defaultValue || ""
    );
    const internalRef = useRef<HTMLDivElement | null>(null);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const [dropdownPosition, setDropdownPosition] = useState({
      top: 0,
      left: 0,
      width: 0,
      maxHeight: 280,
    });

    const currentValue = value !== undefined ? value : internalValue;
    const selectedOption = options.find((opt) => opt.value === currentValue);
    const displayText = selectedOption
      ? (selectedOption.triggerLabel ?? selectedOption.label)
      : placeholder;
    const showValue =
      state === "Filled" || state === "Active" || !!selectedOption;

    // Close dropdown on outside click
    useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
        if (
          isOpen &&
          internalRef.current &&
          dropdownRef.current &&
          !internalRef.current.contains(e.target as Node) &&
          !dropdownRef.current.contains(e.target as Node)
        ) {
          setIsOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    // Close dropdown on scroll (but not when scrolling within the dropdown itself)
    useEffect(() => {
      if (!isOpen) return;

      const handleScroll = (e: Event) => {
        // Don't close if scrolling inside the dropdown
        if (
          dropdownRef.current &&
          dropdownRef.current.contains(e.target as Node)
        ) {
          return;
        }
        setIsOpen(false);
      };

      window.addEventListener("scroll", handleScroll, true);
      return () => window.removeEventListener("scroll", handleScroll, true);
    }, [isOpen]);

    // Calculate dropdown position using the button element
    useEffect(() => {
      if (isOpen && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom - 24; // 24px padding from viewport edge
        const maxAllowedHeight = Math.min(280, spaceBelow); // Cap at 280px or available space

        setDropdownPosition({
          top: rect.bottom + 4, // 4px gap
          left: rect.left,
          width: rect.width,
          maxHeight: Math.max(maxAllowedHeight, 120), // At least 120px to show a few options
        });
      }
    }, [isOpen]);

    const handleToggle = () => {
      if (!disabled) {
        setIsOpen(!isOpen);
      }
    };

    const handleSelect = (optionValue: string) => {
      if (value === undefined) {
        setInternalValue(optionValue);
      }
      onChange?.(optionValue);
      setIsOpen(false);
    };

    // Determine colors based on state
    const getStateStyles = () => {
      if (disabled) {
        return {
          label: "text-[var(--slate-color-action-text-disabled)]", // #757575
          border: "border-[var(--slate-color-action-border-disabled)]", // #dfe0e1
          bg: "bg-[var(--slate-color-action-surface-disabled)]", // #f7f7f7
          text: "text-[var(--slate-color-action-text-disabled)]", // #757575
          icon: "text-[var(--slate-color-action-icon-disabled)]", // #757575
          placeholder: "text-[var(--slate-color-action-text-placeholder)]", // #757575
        };
      }
      if (hasError) {
        return {
          label: "text-[var(--slate-color-action-text-critical)]", // #921100
          border: "border-[var(--slate-color-action-border-critical)]", // #d17e70
          bg: "bg-[var(--slate-color-action-surface-inverse)]", // #ffffff
          text: "text-[var(--slate-color-action-text-default)]", // #333333
          icon: "text-[var(--slate-color-action-icon-default)]", // #333333
          placeholder: "text-[var(--slate-color-action-text-placeholder)]", // #757575
        };
      }
      if (isOpen || state === "Active") {
        return {
          label: "text-[var(--slate-color-action-text-default)]", // #333333
          border: "border-[var(--slate-color-action-border-active)]", // #000000
          bg: "bg-[var(--slate-color-action-surface-inverse)]", // #ffffff
          text: "text-[var(--slate-color-action-text-default)]", // #333333
          icon: "text-[var(--slate-color-action-icon-default)]", // #333333
          placeholder: "text-[var(--slate-color-action-text-placeholder)]", // #757575
        };
      }
      return {
        label: "text-[var(--slate-color-action-text-default)]", // #333333
        border: "border-[var(--slate-color-action-border-subdued)]", // #dfe0e1
        bg: "bg-[var(--slate-color-action-surface-inverse)]", // #ffffff
        text: "text-[var(--slate-color-action-text-default)]", // #333333
        icon: "text-[var(--slate-color-action-icon-default)]", // #333333
        placeholder: "text-[var(--slate-color-action-text-placeholder)]", // #757575
      };
    };

    const stateStyles = getStateStyles();

    return (
      <>
        <div
          ref={(node) => {
            // Assign to internal ref
            internalRef.current = node;
            // Also assign to forwarded ref if provided
            if (typeof ref === "function") {
              ref(node);
            } else if (ref) {
              (ref as React.MutableRefObject<HTMLDivElement | null>).current =
                node;
            }
          }}
          className={cn("flex flex-col", className)}
        >
          {/* Label */}
          {label ? (
            <label
              htmlFor={selectId}
              className={cn(
                "mb-[var(--slate-dimensions-2)]",
                stateStyles.label
              )}
              style={{
                fontFamily: "var(--slate-typography-family-sans-serif)",
                fontSize: "var(--slate-typography-font-size-200)",
                fontWeight: "var(--slate-typography-weight-regular)",
                lineHeight: "var(--slate-typography-line-height-200)",
                letterSpacing: "var(--slate-typography-letter-spacing-200)",
              }}
            >
              {label}
              {required ? (
                <span className="ml-1 text-[var(--slate-color-action-text-critical)]">
                  *
                </span>
              ) : null}
            </label>
          ) : null}

          {/* Select button */}
          <div className="relative w-full">
            <button
              ref={buttonRef}
              id={selectId}
              type="button"
              onClick={handleToggle}
              disabled={disabled}
              aria-label={ariaLabel}
              aria-expanded={isOpen}
              aria-haspopup="listbox"
              className={cn(
                "flex h-[40px] w-full items-center rounded-[var(--slate-radius-component-default)] border px-[var(--slate-dimensions-4)] pr-[40px] text-left transition-all duration-150 ease-in-out",
                stateStyles.border,
                stateStyles.bg,
                disabled ? "cursor-not-allowed" : "cursor-pointer",
                "focus:outline-hidden focus-visible:ring-2 focus-visible:ring-[var(--slate-color-action-border-focus-ring)] focus-visible:ring-offset-2"
              )}
              style={{
                fontFamily: "var(--slate-typography-family-sans-serif)",
                fontSize: "var(--slate-typography-font-size-200)",
                fontWeight: "var(--slate-typography-weight-regular)",
                lineHeight: "var(--slate-typography-line-height-200)",
                letterSpacing: "var(--slate-typography-letter-spacing-200)",
                color: showValue
                  ? "var(--slate-color-action-text-default)"
                  : "var(--slate-color-action-text-placeholder)",
              }}
            >
              {showValue && state === "Active" && selectedOption ? (
                <CheckIcon />
              ) : null}
              <span
                className={
                  showValue && state === "Active" && selectedOption
                    ? "ml-2"
                    : ""
                }
              >
                {displayText}
              </span>
            </button>

            {/* Chevron icon */}
            <div
              className={cn(
                "pointer-events-none absolute top-1/2 right-[10px] -translate-y-1/2",
                stateStyles.icon
              )}
              aria-hidden="true"
            >
              {isOpen || state === "Active" ? (
                <ChevronUpIcon />
              ) : (
                <ChevronDownIcon />
              )}
            </div>
          </div>

          {/* Helper/Error Text */}
          {(helper || hasError) && (
            <span
              id={helperId}
              className={cn(
                "mt-[var(--slate-dimensions-2)]",
                hasError
                  ? "text-[var(--slate-color-action-text-critical)]"
                  : stateStyles.text
              )}
              style={{
                fontFamily: "var(--slate-typography-family-sans-serif)",
                fontSize: "var(--slate-typography-font-size-100)",
                fontWeight: "var(--slate-typography-weight-regular)",
                lineHeight: "var(--slate-typography-line-height-100)",
                letterSpacing: "var(--slate-typography-letter-spacing-200)",
              }}
            >
              {hasError && errorMessage ? errorMessage : helper ? helper : ""}
            </span>
          )}
        </div>

        {/* Dropdown menu (when open) */}
        {isOpen &&
          createPortal(
            <div
              ref={dropdownRef}
              role="listbox"
              className="fixed z-[10000] rounded-[var(--slate-radius-component-default)] border border-[var(--slate-color-action-border-subdued)] bg-[var(--slate-color-action-surface-inverse)] shadow-lg"
              style={{
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: dropdownPosition.width,
                maxHeight: dropdownPosition.maxHeight,
                overflowY: "auto",
                boxShadow: "0 2px 12px rgba(0, 0, 0, 0.15)",
              }}
            >
              {options.map((option) => {
                const isSelected = option.value === currentValue;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handleSelect(option.value)}
                    disabled={option.disabled}
                    className={cn(
                      "flex w-full items-center gap-2 px-[var(--slate-dimensions-4)] py-[var(--slate-dimensions-2)] text-left transition-colors duration-150",
                      isSelected
                        ? "bg-[var(--slate-color-action-surface-inverse-hover)]"
                        : "",
                      option.disabled
                        ? "cursor-not-allowed opacity-50"
                        : "cursor-pointer hover:bg-[var(--slate-color-action-surface-inverse-hover)]",
                      "focus:outline-hidden focus-visible:ring-2 focus-visible:ring-[var(--slate-color-action-border-focus-ring)] focus-visible:ring-offset-2"
                    )}
                    style={{
                      fontFamily: "var(--slate-typography-family-sans-serif)",
                      fontSize: "var(--slate-typography-font-size-200)",
                      fontWeight: "var(--slate-typography-weight-regular)",
                      lineHeight: "var(--slate-typography-line-height-200)",
                      letterSpacing:
                        "var(--slate-typography-letter-spacing-200)",
                      color: "var(--slate-color-action-text-default)",
                    }}
                  >
                    {isSelected ? <CheckIcon /> : null}
                    <span className={isSelected ? "" : "ml-[24px]"}>
                      {option.label}
                    </span>
                  </button>
                );
              })}
            </div>,
            document.body
          )}
      </>
    );
  }
);

Select.displayName = "Select";

export default Select;
